import { IModel, IViolation, IAttribute } from '../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { HDInsightLinkedService } from '../linkedservice/hdinsight.linkedservice';
import { Activity } from './activity';
import { FilePath } from 'app/model/dataset/file-path';

import { createParameterConfigurationValidator } from 'app/model/adapters/linkedservices/parameter-config.util';
import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { HDInsightOnDemandLinkedService } from 'app/model/linkedservice/hdinsightondemand.linkedservice';
import { format } from 'app/shared/utils/string.util';
import { validateFilePath } from 'app/shared/utils/activity-validation.util';
import { AzureBlobStorageLinkedService, AzureSqlDatabaseLinkedService, LinkedService, ModelConstants, sanitizeAndFormatString, stringIsNullOrEmpty } from 'shell-models-lib';
import { Copier } from 'app/model/base/copier';

@Metamodel.model({
    name: ModelConstants.azureHDInsightActivity,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.Hdi_DisplayName),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.Hdi_DisplayName)
    ]
})
export class AzureHDInsightActivity extends Activity {
    // Hive/Pig
    public static readonly SCRIPT_PATH: IAttribute;
    // MapReduce
    public static readonly CLASS_NAME: IAttribute;
    public static readonly JAR_FILE_PATH: IAttribute;
    // Spark
    public static readonly ROOT_PATH: IAttribute;
    public static readonly ENTRY_FILE_PATH: IAttribute;
    public static readonly PROXY_USER: IAttribute;
    public configType: 'Script' | 'Jar';
    // Streaming
    public static readonly MAPPERFILEPATH: IAttribute;
    public static readonly REDUCERFILEPATH: IAttribute;
    public static readonly MAPPER: IAttribute;
    public static readonly REDUCER: IAttribute;
    public static readonly INPUT: IAttribute;
    public static readonly OUTPUT: IAttribute;
    // Common
    public static readonly MODEL: IModel;
    public static readonly ARGUMENTS: IAttribute;
    public static readonly HDI_ACTIVITY_TYPE: IAttribute;


    @Metamodel.attribute({
        display: stringResources.ModelName_ScriptPath,
        association: 'SCRIPT_PATH',
        templatable: true,
        accepts: ['String']
    })
    public scriptPath = new FilePath();

    @Metamodel.attribute({
        display: stringResources.ModelName_EntryFilePath,
        association: 'ENTRY_FILE_PATH',
        templatable: true
    })
    public entryFilePath?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_RootPath,
        association: 'ROOT_PATH',
        templatable: true
    })
    public rootPath?: string;

    @Metamodel.attribute({
        display: stringResources.FilePath_Label,
        templatable: true,
        accepts: ['String'],
        association: 'JAR_FILE_PATH'
    })
    public jarFilePath = new FilePath();

    @Metamodel.attribute({
        display: stringResources.ModelName_Arguments,
        association: 'ARGUMENTS',
        templatable: true,
        accepts: ['Array']
    })
    public azureHDInsightActivityArguments?: ParameterConfig[]; // Property name is "arguments" in JSON, needs to workaround with tslint for banned term.
    public getDebugInfo?: string;

    @Metamodel.attribute({
        display: stringResources.Type,
        association: 'HDI_ACTIVITY_TYPE',
        constraint: {
            required: true,
            validator: AzureHDInsightActivity.validateHDIActivityType
        }
    })

    //Set default as 'HDInsightSpark' out of 
    // 'HDInsightHive'
    // 'HDInsightMapReduce'
    // 'HDInsightPig'
    // 'HDInsightSpark'
    // 'HDInsightStreaming'
    public hdiActivityType: string = 'HDInsightSpark'; 

    private static async validateHDIActivityType(activity: AzureHDInsightActivity, hdiActivityType: string): Promise<IViolation[] | undefined> {
        const violations = [];
        if (hdiActivityType === 'HDInsightHive') {
            const filePathViolation = validateFilePath(activity, activity.scriptPath, AzureHDInsightActivity.SCRIPT_PATH);
            if (filePathViolation.length > 0) {
                violations.push(...filePathViolation);
            }
            if (activity.fileConnection == null) {
                violations.push({
                    message: () =>
                                sanitizeAndFormatString(
                                    stringResources.RequiredTemplate,
                                    stringResources.ModelName_ScriptConnection
                                )
                });
            }
        } else if (hdiActivityType === 'HDInsightPig') {
            const filePathViolation = validateFilePath(activity, activity.scriptPath, AzureHDInsightActivity.SCRIPT_PATH);
            if (filePathViolation.length > 0) {
                violations.push(...filePathViolation);
            }
        } else if (hdiActivityType === 'HDInsightMapReduce') {
            if (stringIsNullOrEmpty(activity.className) && stringIsNullOrEmpty(AzureHDInsightActivity.CLASS_NAME.templateValue(activity))) {
                violations.push({
                    message: () =>
                                sanitizeAndFormatString(
                                    stringResources.RequiredTemplate,
                                    stringResources.MainClassNameLabel
                                )
                });
            }
            const filePathViolation = validateFilePath(activity, activity.jarFilePath, AzureHDInsightActivity.JAR_FILE_PATH);
            if (filePathViolation.length > 0) {
                violations.push(...filePathViolation);
            }
        } else if (hdiActivityType === 'HDInsightSpark') {
            if (stringIsNullOrEmpty(activity.rootPath) && stringIsNullOrEmpty(AzureHDInsightActivity.ROOT_PATH.templateValue(activity))) {
                violations.push({
                    message: () =>
                                sanitizeAndFormatString(
                                    stringResources.RequiredTemplate,
                                    stringResources.ModelName_RootPath
                                )
                });
            }
            if (stringIsNullOrEmpty(activity.entryFilePath) && stringIsNullOrEmpty(AzureHDInsightActivity.ENTRY_FILE_PATH.templateValue(activity))) {
                violations.push({
                    message: () =>
                                sanitizeAndFormatString(
                                    stringResources.RequiredTemplate,
                                    stringResources.ModelName_EntryFilePath
                                )
                });
            }
        } else if (hdiActivityType === 'HDInsightStreaming') {
            if (stringIsNullOrEmpty(activity.reducer) && stringIsNullOrEmpty(AzureHDInsightActivity.REDUCER.templateValue(activity))) {
                violations.push({
                    message: () =>
                                sanitizeAndFormatString(
                                    stringResources.RequiredTemplate,
                                    stringResources.ReducerLabel
                                )
                });
            }
            if (stringIsNullOrEmpty(activity.mapper) && stringIsNullOrEmpty(AzureHDInsightActivity.MAPPER.templateValue(activity))) {
                violations.push({
                    message: () =>
                                sanitizeAndFormatString(
                                    stringResources.RequiredTemplate,
                                    stringResources.ModelName_Mapper
                                )
                });
            }
            const mapperFilePathViolation = validateFilePath(activity, activity.mapperFilePath, AzureHDInsightActivity.MAPPERFILEPATH);
            if (mapperFilePathViolation.length > 0) {
                violations.push(...mapperFilePathViolation);
            }
            const reducerFilePathViolation = validateFilePath(activity, activity.reducerFilePath, AzureHDInsightActivity.REDUCERFILEPATH);
            if (reducerFilePathViolation.length > 0) {
                violations.push(...reducerFilePathViolation);
            }
            if (stringIsNullOrEmpty(activity.input) && stringIsNullOrEmpty(AzureHDInsightActivity.INPUT.templateValue(activity))) {
                violations.push({
                    message: () =>
                                sanitizeAndFormatString(
                                    stringResources.RequiredTemplate,
                                    stringResources.InputLabel
                                )
                });
            }
            if (stringIsNullOrEmpty(activity.output) && stringIsNullOrEmpty(AzureHDInsightActivity.OUTPUT.templateValue(activity))) {
                violations.push({
                    message: () =>
                                sanitizeAndFormatString(
                                    stringResources.RequiredTemplate,
                                    stringResources.OutputLabel
                                )
                });
            }
        }
        return violations;
    }

    private static async validateHDIConnection(activity: AzureHDInsightActivity, linkedService: HDInsightLinkedService | HDInsightOnDemandLinkedService): Promise<IViolation[] | undefined> {
        const violations = [];
        if (linkedService != null) {
            if (linkedService instanceof HDInsightOnDemandLinkedService) {
                if (stringIsNullOrEmpty(activity.clusterType)) {
                    violations.push({
                        message: () =>
                                    sanitizeAndFormatString(
                                        stringResources.RequiredTemplate,
                                        stringResources.ModelName_HdiOnDemand_ClusterType
                                    )
                    });
                }
                if (stringIsNullOrEmpty(activity.timeToLive)) {
                    violations.push({
                        message: () =>
                                    sanitizeAndFormatString(
                                        stringResources.RequiredTemplate,
                                        stringResources.ModelName_HdiOnDemand_TimeToLive
                                    )
                    });
                }
                if (activity.clusterSize == null) {
                    violations.push({
                        message: () =>
                                    sanitizeAndFormatString(
                                        stringResources.RequiredTemplate,
                                        stringResources.ModelName_HdiOnDemand_ClusterSize
                                    )
                    });
                }
            }
        }
        return violations;
    }

    @Metamodel.attribute({
        display: stringResources.ModelName_Defines,
        constraint: {
            validator: createParameterConfigurationValidator()
        }
    })
    public defines?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.VariablesLabel,
        constraint: {
            validator: createParameterConfigurationValidator()
        }
    })
    public variables?: ParameterConfig[];

    public queryTimeout?: number;

    @Metamodel.attribute({
        display: stringResources.ModelName_ClassName,
        templatable: true,
        association: 'CLASS_NAME'
    })
    public className?: string;

    @Metamodel.attribute({
        display: stringResources.SparkActivityProxyUser,
        association: 'PROXY_USER',
        templatable: true
    })
    public proxyUser?: string;

    @Metamodel.attribute({
        display: stringResources.SparkConfiguration,
        constraint: {
            validator: createParameterConfigurationValidator()
        }
    })
    public sparkConfig?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.JarLibsLabel
    })
    public jarLibs: string[] = [];

    @Metamodel.attribute({
        display: stringResources.FilePathForMapperLabel,
        association: 'MAPPERFILEPATH'
    })
    public mapperFilePath = new FilePath();
    @Metamodel.attribute({
        display: stringResources.FilePathForReducerLabel,
        association: 'REDUCERFILEPATH'
    })
    public reducerFilePath = new FilePath();
    @Metamodel.attribute({
        display: stringResources.ModelName_Mapper,
        association: 'MAPPER',
        templatable: true
    })
    public mapper?: string;
    @Metamodel.attribute({
        display: stringResources.ReducerLabel,
        association: 'REDUCER',
        templatable: true
    })
    public reducer?: string;
    @Metamodel.attribute({
        display: stringResources.InputLabel,
        association: 'INPUT',
        templatable: true
    })
    public input?: string;
    @Metamodel.attribute({
        display: stringResources.OutputLabel,
        association: 'OUTPUT',
        templatable: true
    })
    public output?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_HdiOnDemand_ClusterSize
    })
    public clusterSize?: number = 4;

    @Metamodel.attribute({
        display: stringResources.ModelName_HdiOnDemand_TimeToLive
    })
    public timeToLive?: string = '00:15:00';

    @Metamodel.attribute({
        display: stringResources.VersionLabel
    })
    public version?: string = '4.0';

    @Metamodel.attribute({
        display: stringResources.ModelName_HdiOnDemand_ClusterType
    })
    public clusterType?: string = 'hadoop';

    @Metamodel.attribute({
        display: stringResources.ModelName_SparkVersion
    })
    public sparkVersion?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_clusterNamePrefix
    })
    public clusterNamePrefix?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_OsType
    })
    public osType?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_clusterUserName
    })
    public clusterUserName?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_clusterPassword
    })
    public clusterPassword?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_clusterSshUserName
    })
    public clusterSshUserName?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_clusterSshPassword
    })
    public clusterSshPassword?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_ScriptName
    })
    public scriptName?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_ScriptUri
    })
    public scriptUri?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_ScriptNodeTypes
    })
    public scriptNodeTypes?: string[] = [];

    @Metamodel.attribute({
        display: stringResources.ModelName_ScriptParameters
    })
    public scriptParameters?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_HeadNodeSize
    })
    public headNodeSize?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_DataNodeSize
    })
    public dataNodeSize?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_ZookeeperNodeSize
    })
    public zookeeperNodeSize?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_CoreConfig
    })
    public coreConfiguration?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_HBaseConfig
    })
    public hbaseConfiguration?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_HDFSConfig
    })
    public hdfsConfiguration?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_HiveConfig
    })
    public hiveConfiguration?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_MapReduceConfig
    })
    public mapReduceConfiguration?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_OozieConfig
    })
    public oozieConfiguration?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_StormConfig
    })
    public stormConfiguration?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_YarnConfig
    })
    public yarnConfiguration?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.AzureHDInsightActivity_ConnectionTab_HCatalogConnectionLabel,
        reference: true,
        type: AzureSqlDatabaseLinkedService.MODEL
    })
    public hCatalogConnection?: AzureSqlDatabaseLinkedService;

    @Metamodel.attribute({
        display: stringResources.FileConnectionLabel,
        reference: true,
        type: AzureBlobStorageLinkedService.MODEL
    })
    public fileConnection?: AzureBlobStorageLinkedService;

    @Metamodel.attribute({
        display: stringResources.CustomActivitySettingsResourceConnection,
        constraint: {
            required: true
        },
        reference: true,
        type: AzureBlobStorageLinkedService.MODEL
    })
    public resourceConnection?: AzureBlobStorageLinkedService;
    
    @Metamodel.attribute({
        display: stringResources.HDInsight_Connection,
        constraint: {
            required: true,
            validator: AzureHDInsightActivity.validateHDIConnection
        },
        reference: true,
        type: HDInsightLinkedService.MODEL
    })
    public linkedService?: HDInsightLinkedService | HDInsightOnDemandLinkedService;
    public linkedServiceType?: string;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
    }

    public onActivityCloned(clonedActivity: AzureHDInsightActivity, context: { inlineDataset?: boolean }): void {
        if (__IS_FABRIC_DPE__) {
            if (this.linkedService?.parameterized) {
                clonedActivity.linkedService = Copier.clone(
                    context,
                    [this.linkedService],
                    undefined,
                    true
                )[0] as LinkedService;
            }
            if (this.fileConnection?.parameterized) {
                clonedActivity.fileConnection = Copier.clone(
                    context,
                    [this.fileConnection],
                    undefined,
                    true
                )[0] as AzureBlobStorageLinkedService;
            }
            if (this.resourceConnection?.parameterized) {
                clonedActivity.resourceConnection = Copier.clone(
                    context,
                    [this.resourceConnection],
                    undefined,
                    true
                )[0] as AzureBlobStorageLinkedService;
            }
            if (this.hCatalogConnection?.parameterized) {
                clonedActivity.hCatalogConnection = Copier.clone(
                    context,
                    [this.hCatalogConnection],
                    undefined,
                    true
                )[0] as AzureSqlDatabaseLinkedService;
            }
        }
    }
}
